import { baseApi } from "./api";
import { IStaff } from "@/interfaces/Istaff";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const STAFF_ROUTES = {
  GET_STAFF: "/business/staffs",
  ADD_STAFF: "/business/staffs",
  UPDATE_STAFF: "/business/staffs/:id",
  DELETE_STAFF: "/business/staffs/:id",
  UPLOAD_PICTURE: "/business/staffs/:id/profile/picture",
  GET_PICTURE: "/media/profile/picture/:file_name",
};

interface AddStaffRequest {
  staffs: Array<{
    permission_level: string;
    all_business_lines_permitted: boolean;
    permitted_business_lines: string[];
    email: string;
    first_name: string;
    last_name: string;
  }>;
}

const staffApi = {
  uploadPicture: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append("picture_file", file);
    console.log("Uploading file:", {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    });
    console.log("FormData entries:", Array.from(formData.entries()));
    const response = await baseApi.put(
      STAFF_ROUTES.UPLOAD_PICTURE.replace(":id", id),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  },
  getStaffPicture: async (picturePath: string) => {
    console.log("getStaffPicture called with:", picturePath);

    try {
      // Use the picture path directly like contactsApi does
      const response = await baseApi.get(picturePath, {
        responseType: "blob",
      });
      console.log("Staff picture loaded successfully");
      return response;
    } catch (error) {
      console.error("Error in getStaffPicture:", error);
      throw error;
    }
  },
  getStaff: async (query?: string) => {
    const url = query
      ? `${STAFF_ROUTES.GET_STAFF}?q=${encodeURIComponent(query)}`
      : STAFF_ROUTES.GET_STAFF;
    const response = await baseApi.get(url);
    console.log(response);
    return response;
  },

  addStaff: async (staff: {
    email: string;
    permission_level: string;
    all_business_lines_permitted: boolean;
    permitted_business_lines: string[];
    first_name: string;
    last_name: string;
  }) => {
    const requestData: AddStaffRequest = {
      staffs: [staff],
    };
    console.log("Request data:", requestData);
    const response = await baseApi.post(STAFF_ROUTES.ADD_STAFF, requestData);
    console.log("Response:", response);
    return response;
  },

  updateStaff: async (id: string, staff: IStaff) => {
    try {
      const response = await baseApi.put(
        STAFF_ROUTES.UPDATE_STAFF.replace(":id", id),
        staff
      );
      return response;
    } catch (error) {
      console.error("Error in updateStaff:", error);
      throw error;
    }
  },

  deleteStaff: async (id: string) => {
    try {
      const response = await baseApi.delete(
        STAFF_ROUTES.DELETE_STAFF.replace(":id", id)
      );
      return response;
    } catch (error) {
      console.error("Error in deleteStaff:", error);
      throw error;
    }
  },
};

// React Query hooks

export const staffApiHooks = {
  useUploadPicture: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, file }: { id: string; file: File }) =>
        staffApi.uploadPicture(id, file),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["staff"] });
      },
    });
  },
  useStaff: (query?: string) => {
    return useQuery({
      queryKey: ["staff", query],
      queryFn: () => staffApi.getStaff(query),
    });
  },
  useAddStaff: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: staffApi.addStaff,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["staff"] });
      },
    });
  },
  useUpdateStaff: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, staff }: { id: string; staff: IStaff }) =>
        staffApi.updateStaff(id, staff),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["staff"] });
      },
    });
  },
  useDeleteStaff: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: staffApi.deleteStaff,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["staff"] });
      },
    });
  },
};

export function useAddStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: staffApi.addStaff,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
    },
  });
}

export function useUpdateStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, staff }: { id: string; staff: IStaff }) =>
      staffApi.updateStaff(id, staff),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
    },
  });
}

export function useDeleteStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => staffApi.deleteStaff(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
    },
  });
}

export default staffApi;
